import type { Directive, DirectiveBinding } from 'vue'
import { usePermissions, type ActionName, type ModuleName } from '@/composables/usePermissions'

/**
 * Directive v-permission pour afficher/masquer des éléments selon les permissions.
 *
 * Usage:
 * ```html
 * <!-- Syntaxe avec argument et modificateur -->
 * <button v-permission:create.project>Créer</button>
 * <button v-permission:export.finance>Exporter</button>
 * <button v-permission:edit.hr>Modifier</button>
 *
 * <!-- Syntaxe avec valeur (pour resourceCelluleId) -->
 * <button v-permission="{ action: 'edit', module: 'project', resourceCelluleId: 123 }">
 *   Modifier
 * </button>
 * ```
 */

interface PermissionValue {
  action: ActionName
  module: ModuleName
  resourceCelluleId?: number
}

function checkPermission(el: HTMLElement, binding: DirectiveBinding) {
  const { can } = usePermissions()

  let action: ActionName
  let module: ModuleName
  let resourceCelluleId: number | undefined

  if (typeof binding.value === 'object' && binding.value !== null) {
    // Syntaxe avec objet: v-permission="{ action: 'create', module: 'project' }"
    const value = binding.value as PermissionValue
    action = value.action
    module = value.module
    resourceCelluleId = value.resourceCelluleId
  } else {
    // Syntaxe avec argument et modificateur: v-permission:create.project
    action = binding.arg as ActionName
    // Le modificateur est le module (ex: .project, .finance)
    const modifierKeys = Object.keys(binding.modifiers)
    module = modifierKeys[0] as ModuleName
  }

  if (!action || !module) {
    console.warn('[v-permission] Action et module requis')
    return
  }

  const hasPermission = can(action, module, resourceCelluleId)

  if (!hasPermission) {
    // Masquer l'élément
    el.style.display = 'none'
  } else {
    // Restaurer l'affichage (utilise la valeur originale si stockée)
    el.style.display = (el as any)._originalDisplay ?? ''
  }
}

export const vPermission: Directive = {
  beforeMount(el: HTMLElement) {
    // Stocker le display original
    ;(el as any)._originalDisplay = el.style.display
  },
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    checkPermission(el, binding)
  },
  updated(el: HTMLElement, binding: DirectiveBinding) {
    checkPermission(el, binding)
  },
}

export default vPermission
